import { ColTypeDef, ICellRendererParams } from "ag-grid-community";
import React from "react";

import { ProgressBar, ProgressBarProps } from "../../../atoms/ProgressBar";
import { Text } from "../../../atoms/Text";

export type ProgressCellRendererParams = {
	/** Color for the progress bar */
	color?: ProgressBarProps["color"];
	/** Override color with an explicit hex/CSS color value instead of using the palette */
	colorOverride?: ProgressBarProps["colorOverride"];
	/** Whether the progress value is already a percentage (0-100) or needs to be multiplied by 100 */
	isPercentage?: boolean;
	/** Accessible label forwarded to the underlying ProgressBar */
	ariaLabel?: string;
};

/**
 * Formats a percentage value for display, capping extremely high/low values
 * @param percentage - The percentage value to format
 * @returns The formatted display value (number or string like "<-999" or ">999")
 */
const formatPercentageDisplay = (percentage: number): number | string => {
	if (percentage < -9.99) return "<-999";
	if (percentage > 999) return ">999";
	return Math.floor(percentage);
};

// Custom cell renderer for progress bars
// Expects value to be a number (progress value)
// Configuration passed via cellRendererParams (can be static object or function)
export const ProgressCellRenderer = <T = unknown,>(params: ICellRendererParams<T>) => {
	const progress = params.value;

	// Handle null/undefined values
	if (typeof progress !== "number") {
		return null;
	}

	// Extract configuration from cellRendererParams
	// Support both function and object formats
	const cellRendererParams = params.colDef?.cellRendererParams;
	const rendererParams =
		typeof cellRendererParams === "function"
			? cellRendererParams(params.data as T)
			: (cellRendererParams as ProgressCellRendererParams | undefined);

	const color = rendererParams?.color ?? undefined;
	const colorOverride = rendererParams?.colorOverride;
	const isPercentage = rendererParams?.isPercentage ?? progress > 1;
	const ariaLabel = rendererParams?.ariaLabel;

	// Convert to percentage if needed (assuming value is 0-1)
	const percentage = isPercentage ? progress : progress * 100;

	return (
		<div className="grid size-full grid-cols-[1fr_auto] items-center gap-2">
			<ProgressBar
				value={Math.min(Math.max(percentage, 0), 100)}
				color={color}
				colorOverride={colorOverride}
				size={8}
				aria-label={ariaLabel}
			/>
			<Text size="paragraph-3" weight="regular" dangerouslySetClassName="w-9 py-line-clamp-1">
				{formatPercentageDisplay(percentage)}%
			</Text>
		</div>
	);
};

const ProgressCol: ColTypeDef = {
	cellRenderer: ProgressCellRenderer,
	comparator: (valueA, valueB) => {
		// Simple numeric comparison since values are now just numbers
		const a = typeof valueA === "number" ? valueA : 0;
		const b = typeof valueB === "number" ? valueB : 0;
		return a - b;
	},
};

export default ProgressCol;
